// Business hours + escalation settings (Settings → Business hours).
import { getHours, saveHours, timezoneFor, isOpenAt, localClock, DAY_KEYS, DAY_LABELS, OFF_HOURS_MODES, NOTIFY_OPTIONS } from "../hours";
import { ok, validationFail } from "../helpers";
import { collect, firstMessage, intRangeError, lengthError, oneOfError } from "../validate";

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

function view(tenantId) {
  const doc = getHours(tenantId);
  const tz = timezoneFor(tenantId);
  return { ...doc, timezone: tz, isOpenNow: isOpenAt(doc, tz), localTime: localClock(tz).label };
}

function validate(body) {
  const fields = {};
  if (!Array.isArray(body.days) || body.days.length !== 7) {
    fields.days = "Provide opening hours for all seven days.";
  } else {
    body.days.forEach((d, i) => {
      const label = DAY_LABELS[DAY_KEYS[i]];
      if (!d || d.day !== DAY_KEYS[i]) { fields.days = fields.days || `Days must be listed Monday to Sunday (problem at position ${i + 1}).`; return; }
      if (typeof d.open !== "boolean") { fields[`days.${d.day}`] = `${label}: choose open or closed.`; return; }
      if (!d.open) return;
      if (!HHMM.test(String(d.from || "")) || !HHMM.test(String(d.to || ""))) fields[`days.${d.day}`] = `${label}: enter times as HH:MM (for example 09:00).`;
      else if (d.from >= d.to) fields[`days.${d.day}`] = `${label}: closing time must be after opening time.`;
    });
    const mode = (body.offHours || {}).mode;
    if (!fields.days && !Object.keys(fields).length && !body.days.some((d) => d.open) && mode !== "offline") fields.days = "Open at least one day, or choose \"Show an offline message\" so visitors know you are closed.";
  }
  const off = body.offHours || {};
  const esc = body.escalation || {};
  Object.assign(fields, collect([
    ["offHours.mode", oneOfError(off.mode, OFF_HOURS_MODES, "Off-hours behaviour")],
    off.mode === "offline" ? ["offHours.offlineMessage", lengthError(off.offlineMessage, { min: 10, max: 300, label: "Offline message", required: true })] : null,
    ["escalation.waitAlertMinutes", intRangeError(esc.waitAlertMinutes, { min: 1, max: 120, label: "Wait-time alert" })],
    ["escalation.notify", oneOfError(esc.notify, NOTIFY_OPTIONS, "Escalation recipients")]
  ].filter(Boolean)) || {});
  return Object.keys(fields).length ? fields : null;
}

export const hoursRoutes = [
  {
    method: "GET", pattern: /^\/api\/business-hours$/, permission: "business:manage",
    handler: ({ tenantId }) => ok(200, view(tenantId))
  },
  {
    method: "PUT", pattern: /^\/api\/business-hours$/, permission: "business:manage",
    handler: ({ tenantId, body }) => {
      const fields = validate(body);
      if (fields) return validationFail(fields, firstMessage(fields));
      saveHours(tenantId, {
        days: body.days.map((d) => ({ day: d.day, open: d.open, from: d.open ? d.from : (d.from || "09:00"), to: d.open ? d.to : (d.to || "18:00") })),
        offHours: { mode: body.offHours.mode, offlineMessage: String(body.offHours.offlineMessage || "").trim() },
        escalation: { waitAlertMinutes: Number(body.escalation.waitAlertMinutes), notify: body.escalation.notify }
      });
      return ok(200, view(tenantId));
    }
  }
];
